/**
 * M-A13 — Skill group-restrict modal.
 *
 * Sprint 133 Stream C. Manages ``Skill#users`` / ``Skill#editors`` grants
 * via Keto subject_set on ``Group#members`` by calling the admin endpoint
 * ``/admin/skills/{skill_id}/group-grants``.
 *
 * Note: granting ``Skill#users`` to a Group is **additive** — it does NOT
 * remove the broader ``Tenant#members`` subject_set inherited via OPL. To
 * enforce strict scoping (only group members may use the skill, denying
 * everyone else), the tenant-wide grant must be revoked separately. That
 * stricter mode is out of scope for this modal.
 */

import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { IconButton } from '@/components/ui/icon';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useGroups } from '@/hooks/use-groups';
import {
  useSkillGroupGrants,
  useAddSkillGroupGrant,
  useRemoveSkillGroupGrant,
  type SkillGroupGrant,
  type SkillGroupRelation,
} from '@/features/skills';
import { useI18n } from '@/i18n';
import { getErrorMessage } from '@/lib/api-client';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  skillId: string;
}

export function SkillRestrictUseModal({ open, onOpenChange, skillId }: Props) {
  const grantsQuery = useSkillGroupGrants(open ? skillId : undefined);
  const groupsQuery = useGroups();
  const addGrant = useAddSkillGroupGrant(skillId);
  const removeGrant = useRemoveSkillGroupGrant(skillId);

  const [groupId, setGroupId] = useState<string>('');
  const [relation, setRelation] = useState<SkillGroupRelation>('users');

  const { t } = useI18n();

  const grants: SkillGroupGrant[] = grantsQuery.data?.grants ?? [];
  const groups = groupsQuery.data?.items ?? [];
  const groupNameById = useMemo(() => {
    const m = new Map<string, string>();
    groups.forEach((g) => m.set(g.id, g.name));
    return m;
  }, [groups]);

  const alreadyGranted = grants.some(
    (g) => g.group_id === groupId && g.relation === relation,
  );
  const canAdd = Boolean(groupId) && !alreadyGranted;

  function relationLabel(r: SkillGroupRelation): string {
    return r === 'editors'
      ? t('admin.skills.restrictUse.badgeEdit')
      : t('admin.skills.restrictUse.badgeUse');
  }

  async function handleAdd() {
    if (!canAdd) return;
    try {
      await addGrant.mutateAsync({ group_id: groupId, relation });
      toast.success(
        t('admin.skills.restrictUse.granted', {
          relation: relationLabel(relation),
          groupName: groupNameById.get(groupId) ?? groupId,
        }),
      );
      setGroupId('');
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  async function handleRemove(grant: SkillGroupGrant) {
    const label = groupNameById.get(grant.group_id) ?? grant.group_id;
    if (
      !confirm(
        t('admin.skills.restrictUse.confirmRevoke', {
          relation: relationLabel(grant.relation),
          groupName: label,
        }),
      )
    ) {
      return;
    }
    try {
      await removeGrant.mutateAsync({
        group_id: grant.group_id,
        relation: grant.relation,
      });
      toast.success(
        t('admin.skills.restrictUse.revoked', {
          relation: relationLabel(grant.relation),
          groupName: label,
        }),
      );
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('admin.skills.restrictUse.title')}</DialogTitle>
          <DialogDescription>
            {t('admin.skills.restrictUse.description')}
          </DialogDescription>
        </DialogHeader>

        {/* Existing grants */}
        <div className="space-y-2">
          <Label>{t('admin.skills.restrictUse.currentGrants')}</Label>
          {grantsQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">{t('admin.skills.restrictUse.loading')}</p>
          ) : grants.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t('admin.skills.restrictUse.noGrants')}
            </p>
          ) : (
            <ul className="space-y-1">
              {grants.map((g) => (
                <li
                  key={`${g.relation}:${g.group_id}`}
                  className="flex items-center justify-between rounded border px-3 py-2"
                >
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">
                      {relationLabel(g.relation)}
                    </Badge>
                    <span className="text-sm font-medium">
                      {groupNameById.get(g.group_id) ?? g.group_id}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {g.group_id}
                    </span>
                  </div>
                  <IconButton
                    icon="trash"
                    label={t('admin.skills.restrictUse.revokeLabel', {
                      relation: relationLabel(g.relation),
                      groupId: g.group_id,
                    })}
                    onClick={() => handleRemove(g)}
                    disabled={removeGrant.isPending}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Add new grant */}
        <div className="space-y-2 border-t pt-4">
          <Label>{t('admin.skills.restrictUse.addGrant')}</Label>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
            <div className="flex-1 space-y-1">
              <Label htmlFor="grant-group" className="text-xs">
                {t('admin.skills.restrictUse.group')}
              </Label>
              <Select value={groupId} onValueChange={setGroupId}>
                <SelectTrigger id="grant-group">
                  <SelectValue placeholder={t('admin.skills.restrictUse.selectGroup')} />
                </SelectTrigger>
                <SelectContent>
                  {groups.map((g) => (
                    <SelectItem key={g.id} value={g.id}>
                      {g.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="grant-relation" className="text-xs">
                {t('admin.skills.restrictUse.relation')}
              </Label>
              <Select
                value={relation}
                onValueChange={(v) => setRelation(v as SkillGroupRelation)}
              >
                <SelectTrigger id="grant-relation" className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="users">{t('admin.skills.restrictUse.relationUse')}</SelectItem>
                  <SelectItem value="editors">{t('admin.skills.restrictUse.relationEdit')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={handleAdd}
              disabled={!canAdd || addGrant.isPending}
            >
              {addGrant.isPending ? t('admin.skills.restrictUse.adding') : t('admin.skills.restrictUse.addGrant')}
            </Button>
          </div>
          {alreadyGranted && (
            <p className="text-xs text-destructive">
              {t('admin.skills.restrictUse.alreadyGranted')}
            </p>
          )}
        </div>

        {grants.length > 0 && (
          <p className="text-xs text-muted-foreground">
              {t('admin.skills.restrictUse.warning')}
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('admin.skills.restrictUse.done')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default SkillRestrictUseModal;
